// Presentation rules of the email-failures panel (spec 001, RF-4). Pure functions, no React:
// the business rules (single-use retry, closing, roles) live in the backend.

export const EMAIL_ADMIN_ROLES = ["admin", "tech_admin"];

export function canSeeEmailFailures(user) {
  return !!user && EMAIL_ADMIN_ROLES.includes(user.role);
}

const RESULT_LABELS = { sent: "enviado", closed: "falló definitivamente" };

export function resultLabel(result) {
  return RESULT_LABELS[result] || "aún sin resultado";
}

// How often the panel asks again while a manual retry is running (RF-4).
export const RETRY_POLL_MILLISECONDS = 5000;

export function isRetrying(item) {
  return item?.state === "retrying";
}

export function shouldPoll(items) {
  return (items || []).some(isRetrying);
}

function hourOf(value) {
  if (!value) return "";
  return new Date(value).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

// "Reintento en curso, solicitado por [persona] a las [hora]", shown instead of the button (RF-4).
export function retryingLabel(item) {
  const who = item?.manual_retry_by_name || "otra persona";
  const at = hourOf(item?.manual_retry_at);
  return `Reintento en curso, solicitado por ${who}${at ? ` a las ${at}` : ""}`;
}

// Ids that were running a retry before and are not anymore: they ended (sent or closed) (RF-4).
export function finishedRetries(previousItems, nextItems) {
  const running = new Set((nextItems || []).filter(isRetrying).map((item) => item.id));
  return (previousItems || []).filter((item) => isRetrying(item) && !running.has(item.id)).map((item) => item.id);
}

// Result of a retry that ended after the answer was "en curso", from its last state change (RF-4).
export function finishedRetryNotice(state) {
  if (state === "sent") return { tone: "ok", text: "El reintento terminó: el correo se envió correctamente." };
  if (state === "closed") {
    return { tone: "error", text: "El reintento terminó: el envío falló definitivamente y ya no se puede reintentar." };
  }
  return { tone: "info", text: "El reintento terminó." };
}

const PAUSE_REASONS = {
  credentials_missing: "faltan las credenciales de Microsoft 365 en la configuración",
  credentials_rejected: "Microsoft 365 rechazó la credencial de la cuenta de envío",
  unauthorized: "Microsoft 365 respondió «no autorizado» al enviar",
  forbidden: "Microsoft 365 respondió «prohibido» al enviar",
};

// Text of the "envío de correo en pausa" warning; the reason is a code, never the secret (RF-18, RNF-9).
export function pauseNotice(queuePaused) {
  if (!queuePaused) return null;
  const reason = PAUSE_REASONS[queuePaused.reason_code] || `falla de configuración (${queuePaused.reason_code || "sin código"})`;
  const since = queuePaused.since
    ? new Date(queuePaused.since).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "";
  return {
    title: "Envío de correo en pausa",
    text: `Los correos se siguen registrando y saldrán en orden cuando se corrija la configuración: ${reason}${since ? `, desde ${since}` : ""}.`,
  };
}

// -> { remove, keepBusy, reload, tone: "ok" | "error" | "info", text }
//   remove:   the row leaves the list (the alert disappears)
//   keepBusy: the row stays but cannot be retried again (a retry is running)
//   reload:   ask the list again to show the "reintento en curso" label
export function interpretRetryResponse(httpStatus, data = {}) {
  const status = data.status;
  if (status === "sent") return { remove: true, keepBusy: false, reload: false, tone: "ok", text: data.detail };
  if (status === "closed") return { remove: true, keepBusy: false, reload: false, tone: "error", text: data.detail };
  if (status === "in_progress") return { remove: false, keepBusy: true, reload: true, tone: "info", text: data.detail };
  if (status === "already_retried") {
    const who = data.retried_by_name ? ` por ${data.retried_by_name}` : "";
    if (data.situation === "in_progress") {
      return {
        remove: false, keepBusy: true, reload: true, tone: "info",
        text: `${data.detail.replace(/\.$/, "")}${who}; sigue en curso.`,
      };
    }
    return {
      remove: true, keepBusy: false, reload: false, tone: "info",
      text: `${data.detail} Resultado: ${resultLabel(data.previous_result || data.situation)}.`,
    };
  }
  if (status === "not_retryable" || status === "not_found") {
    return { remove: true, keepBusy: false, reload: false, tone: "info", text: data.detail };
  }
  return { remove: false, keepBusy: false, reload: false, tone: "error", text: data.detail || `Error ${httpStatus}` };
}

export function interpretRetryError(error) {
  const response = error?.response;
  if (!response) {
    return {
      remove: false, keepBusy: false, reload: false, tone: "error",
      text: "No se pudo conectar con el servidor. Revisa tu conexión e intenta de nuevo.",
    };
  }
  const data = response.data || {};
  if (data.status) return interpretRetryResponse(response.status, data);
  return { remove: false, keepBusy: false, reload: false, tone: "error", text: data.detail || `Error ${response.status}` };
}
